import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePortfolio } from '../../context/PortfolioContext';
import { getComplianceCheck, getComplianceRiskMetrics, getComplianceRules, saveComplianceRule, deleteComplianceRule } from '../../services/api';
import { ShieldCheck, ShieldAlert, AlertTriangle, Trash2, Plus, ToggleLeft, ToggleRight } from 'lucide-react';
import toast from 'react-hot-toast';
// @ts-ignore
import Spinner from '../../components/Spinner';

interface PositionViolation {
  ticker: string;
  weight_pct: number;
  limit_pct: number;
  excess_pct: number;
}

interface SectorViolation {
  sector: string;
  weight_pct: number;
  limit_pct: number;
  excess_pct: number;
}

interface CustomRuleResult {
  rule: { name: string; type: string; threshold: number };
  is_compliant: boolean;
  details: string;
}

interface ComplianceCheckData {
  overall_compliant: boolean;
  position_concentration: {
    violations: PositionViolation[];
    is_compliant: boolean;
  };
  sector_concentration: {
    violations: SectorViolation[];
    is_compliant: boolean;
  };
  custom_rules: CustomRuleResult[];
}

interface RiskMetricsData {
  var: {
    var_pct: number;
    var_dollar: number;
    confidence: number;
  };
  correlation: {
    labels: string[];
    matrix: number[][];
  };
  portfolio_beta: number;
  total_value: number;
}

interface ComplianceRule {
  id: string;
  name: string;
  type: string;
  threshold: number;
  enabled: boolean;
}

const SurveillanceView: React.FC = () => {
  const { currentUser } = useAuth();
  const { activePortfolioId } = usePortfolio();

  const [loading, setLoading] = useState(true);
  const [complianceData, setComplianceData] = useState<ComplianceCheckData | null>(null);
  const [riskMetrics, setRiskMetrics] = useState<RiskMetricsData | null>(null);
  const [rules, setRules] = useState<ComplianceRule[]>([]);

  // Add Rule form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRuleName, setNewRuleName] = useState('');
  const [newRuleType, setNewRuleType] = useState('max_position_pct');
  const [newRuleThreshold, setNewRuleThreshold] = useState('');
  const [savingRule, setSavingRule] = useState(false);

  const fetchAllData = useCallback(async () => {
    if (!currentUser || !activePortfolioId) return;
    setLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const [checkRes, metricsRes, rulesRes] = await Promise.all([
        getComplianceCheck(token, activePortfolioId),
        getComplianceRiskMetrics(token, activePortfolioId),
        getComplianceRules(token),
      ]);
      setComplianceData(checkRes);
      setRiskMetrics(metricsRes);
      setRules(rulesRes.rules || []);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [currentUser, activePortfolioId]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleToggleRule = async (rule: ComplianceRule) => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      await saveComplianceRule(token, { ...rule, enabled: !rule.enabled });
      setRules(prev =>
        prev.map(r => (r.id === rule.id ? { ...r, enabled: !r.enabled } : r))
      );
      toast.success(`Rule "${rule.name}" ${!rule.enabled ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to toggle rule');
    }
  };

  const handleDeleteRule = async (rule: ComplianceRule) => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      await deleteComplianceRule(token, rule.id);
      setRules(prev => prev.filter(r => r.id !== rule.id));
      toast.success(`Rule "${rule.name}" deleted`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete rule');
    }
  };

  const handleSaveRule = async () => {
    if (!currentUser || !newRuleName.trim() || !newRuleThreshold.trim()) return;
    setSavingRule(true);
    try {
      const token = await currentUser.getIdToken();
      const result = await saveComplianceRule(token, {
        name: newRuleName.trim(),
        type: newRuleType,
        threshold: parseFloat(newRuleThreshold),
        enabled: true,
      });
      setRules(prev => [...prev, result.rule || result]);
      setNewRuleName('');
      setNewRuleType('max_position_pct');
      setNewRuleThreshold('');
      setShowAddForm(false);
      toast.success('Rule created');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save rule');
    } finally {
      setSavingRule(false);
    }
  };

  const getCorrelationColor = (value: number): string => {
    // blue (-1) -> white (0) -> red (+1)
    if (value >= 0) {
      const intensity = Math.round(value * 255);
      return `rgb(255, ${255 - intensity}, ${255 - intensity})`;
    } else {
      const intensity = Math.round(Math.abs(value) * 255);
      return `rgb(${255 - intensity}, ${255 - intensity}, 255)`;
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (!activePortfolioId) {
    return (
      <div className="h-full overflow-y-auto bg-slate-50">
        <div className="max-w-7xl mx-auto p-6 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 text-center text-slate-500">
            <ShieldAlert className="mx-auto mb-3 text-slate-400" size={40} />
            <p className="text-lg font-semibold">No Portfolio Selected</p>
            <p className="text-sm mt-1">Select a portfolio to view compliance and risk data.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full overflow-y-auto bg-slate-50 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">

        {/* Section 1: Status Banner */}
        {complianceData && (
          complianceData.overall_compliant ? (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl p-4 flex items-center gap-3">
              <ShieldCheck size={24} />
              <div>
                <p className="font-bold text-lg">All Clear</p>
                <p className="text-sm">Your portfolio is fully compliant with all defined rules and concentration limits.</p>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 flex items-center gap-3">
              <ShieldAlert size={24} />
              <div>
                <p className="font-bold text-lg">Violations Detected</p>
                <p className="text-sm">One or more compliance rules have been breached. Review the details below.</p>
              </div>
            </div>
          )
        )}

        {/* Section 2: Two-column concentration violations */}
        {complianceData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Position Concentration */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-500" />
                Position Concentration
              </h3>
              {complianceData.position_concentration.violations.length === 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-emerald-700 text-sm flex items-center gap-2">
                  <ShieldCheck size={16} />
                  No violations - all positions within limits.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="text-left px-4 py-2 font-semibold text-slate-600">Ticker</th>
                        <th className="text-right px-4 py-2 font-semibold text-slate-600">Weight %</th>
                        <th className="text-right px-4 py-2 font-semibold text-slate-600">Limit %</th>
                        <th className="text-right px-4 py-2 font-semibold text-slate-600">Excess %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {complianceData.position_concentration.violations.map((v, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-2 font-medium text-slate-800">{v.ticker}</td>
                          <td className="px-4 py-2 text-right text-slate-600">{v.weight_pct.toFixed(2)}%</td>
                          <td className="px-4 py-2 text-right text-slate-600">{v.limit_pct.toFixed(2)}%</td>
                          <td className="px-4 py-2 text-right text-red-600 font-bold">{v.excess_pct.toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Sector Concentration */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <AlertTriangle size={18} className="text-amber-500" />
                Sector Concentration
              </h3>
              {complianceData.sector_concentration.violations.length === 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-emerald-700 text-sm flex items-center gap-2">
                  <ShieldCheck size={16} />
                  No violations - all sectors within limits.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="text-left px-4 py-2 font-semibold text-slate-600">Sector</th>
                        <th className="text-right px-4 py-2 font-semibold text-slate-600">Weight %</th>
                        <th className="text-right px-4 py-2 font-semibold text-slate-600">Limit %</th>
                        <th className="text-right px-4 py-2 font-semibold text-slate-600">Excess %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {complianceData.sector_concentration.violations.map((v, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-2 font-medium text-slate-800">{v.sector}</td>
                          <td className="px-4 py-2 text-right text-slate-600">{v.weight_pct.toFixed(2)}%</td>
                          <td className="px-4 py-2 text-right text-slate-600">{v.limit_pct.toFixed(2)}%</td>
                          <td className="px-4 py-2 text-right text-red-600 font-bold">{v.excess_pct.toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Section 2b: Custom Rule Results */}
        {complianceData && complianceData.custom_rules && complianceData.custom_rules.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-slate-800 mb-4">Custom Rule Results</h3>
            <div className="space-y-2">
              {complianceData.custom_rules.map((cr, idx) => (
                <div
                  key={idx}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    cr.is_compliant
                      ? 'bg-emerald-50 border-emerald-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {cr.is_compliant ? (
                      <ShieldCheck size={16} className="text-emerald-600" />
                    ) : (
                      <ShieldAlert size={16} className="text-red-600" />
                    )}
                    <div>
                      <p className={`text-sm font-semibold ${cr.is_compliant ? 'text-emerald-800' : 'text-red-800'}`}>
                        {cr.rule.name}
                      </p>
                      <p className={`text-xs ${cr.is_compliant ? 'text-emerald-600' : 'text-red-600'}`}>
                        {cr.details}
                      </p>
                    </div>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded ${
                    cr.is_compliant ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {cr.is_compliant ? 'PASS' : 'FAIL'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section 3: Risk KPI Cards */}
        {riskMetrics && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* VaR 95% */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                VaR {riskMetrics.var.confidence}%
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {formatCurrency(riskMetrics.var.var_dollar)}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {riskMetrics.var.var_pct.toFixed(2)}% of portfolio
              </p>
            </div>

            {/* Portfolio Beta */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Portfolio Beta
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {riskMetrics.portfolio_beta.toFixed(2)}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {riskMetrics.portfolio_beta > 1 ? 'Higher' : 'Lower'} volatility than market
              </p>
            </div>

            {/* Total Value */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Total Portfolio Value
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {formatCurrency(riskMetrics.total_value)}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                Current market value
              </p>
            </div>
          </div>
        )}

        {/* Section 4: Correlation Heatmap */}
        {riskMetrics && riskMetrics.correlation && riskMetrics.correlation.labels.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h3 className="font-bold text-slate-800 mb-4">Correlation Matrix</h3>
            <div className="overflow-x-auto">
              <div
                className="inline-grid gap-px"
                style={{
                  gridTemplateColumns: `80px repeat(${riskMetrics.correlation.labels.length}, 56px)`,
                }}
              >
                {/* Top-left empty cell */}
                <div />

                {/* Column headers */}
                {riskMetrics.correlation.labels.map((label) => (
                  <div
                    key={`col-${label}`}
                    className="text-[10px] font-semibold text-slate-600 text-center truncate px-1"
                    title={label}
                  >
                    {label}
                  </div>
                ))}

                {/* Rows */}
                {riskMetrics.correlation.matrix.map((row, rowIdx) => (
                  <React.Fragment key={`row-${rowIdx}`}>
                    {/* Row header */}
                    <div className="text-[10px] font-semibold text-slate-600 truncate flex items-center pr-2" title={riskMetrics.correlation.labels[rowIdx]}>
                      {riskMetrics.correlation.labels[rowIdx]}
                    </div>

                    {/* Cells */}
                    {row.map((value, colIdx) => (
                      <div
                        key={`cell-${rowIdx}-${colIdx}`}
                        className="w-14 h-10 flex items-center justify-center text-[10px] font-medium border border-slate-100 rounded-sm"
                        style={{ backgroundColor: getCorrelationColor(value) }}
                        title={`${riskMetrics.correlation.labels[rowIdx]} / ${riskMetrics.correlation.labels[colIdx]}: ${value.toFixed(2)}`}
                      >
                        {value.toFixed(2)}
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
            {/* Legend */}
            <div className="flex items-center gap-2 mt-4 text-xs text-slate-500">
              <span>-1</span>
              <div className="h-3 w-32 rounded" style={{ background: 'linear-gradient(to right, rgb(0,0,255), rgb(255,255,255), rgb(255,0,0))' }} />
              <span>+1</span>
            </div>
          </div>
        )}

        {/* Section 5: Custom Rules Manager */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-800">Custom Compliance Rules</h3>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white text-sm font-semibold rounded-lg hover:bg-slate-900 transition-colors"
            >
              <Plus size={14} />
              Add Rule
            </button>
          </div>

          {/* Add Rule Form */}
          {showAddForm && (
            <div className="bg-slate-50 p-4 rounded-lg border mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Rule Name</label>
                  <input
                    type="text"
                    value={newRuleName}
                    onChange={(e) => setNewRuleName(e.target.value)}
                    placeholder="e.g. Max Single Position"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Type</label>
                  <select
                    value={newRuleType}
                    onChange={(e) => setNewRuleType(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white"
                  >
                    <option value="max_position_pct">Max Position %</option>
                    <option value="max_holdings">Max Holdings</option>
                    <option value="min_holdings">Min Holdings</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Threshold</label>
                  <input
                    type="number"
                    value={newRuleThreshold}
                    onChange={(e) => setNewRuleThreshold(e.target.value)}
                    placeholder="e.g. 10"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <button
                    onClick={handleSaveRule}
                    disabled={savingRule || !newRuleName.trim() || !newRuleThreshold.trim()}
                    className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {savingRule ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddForm(false);
                      setNewRuleName('');
                      setNewRuleType('max_position_pct');
                      setNewRuleThreshold('');
                    }}
                    className="px-4 py-2 bg-white text-slate-600 text-sm font-semibold rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Rules List */}
          {rules.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">No custom rules defined yet. Click "Add Rule" to create one.</p>
          ) : (
            <div className="space-y-2">
              {rules.map((rule) => (
                <div
                  key={rule.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleToggleRule(rule)}
                      className="flex-shrink-0 text-slate-500 hover:text-slate-800 transition-colors"
                      title={rule.enabled ? 'Disable rule' : 'Enable rule'}
                    >
                      {rule.enabled ? (
                        <ToggleRight size={24} className="text-emerald-600" />
                      ) : (
                        <ToggleLeft size={24} className="text-slate-400" />
                      )}
                    </button>
                    <div>
                      <p className={`text-sm font-semibold ${rule.enabled ? 'text-slate-800' : 'text-slate-400'}`}>
                        {rule.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {rule.type} &middot; Threshold: {rule.threshold}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteRule(rule)}
                    className="flex-shrink-0 p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete rule"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default SurveillanceView;
